import { NgIf, NgTemplateOutlet } from '@angular/common';
import {
    Input,
    OnInit,
    inject,
    OnChanges,
    Component,
    TemplateRef,
    SimpleChanges,
    ChangeDetectorRef,
    ChangeDetectionStrategy,
} from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faSpinner, faDownload } from '@fortawesome/pro-regular-svg-icons';

import { LazyLoadQueueService } from './lazy-load-queue.service';

export interface LazyLoadValue<T = unknown> {
    url: string;
    type: 'lazyLoad';
    _loadedData?: T;
}

export function isLazyLoadValue(value: unknown): value is LazyLoadValue {
    return (
        typeof value === 'object' &&
        value !== null &&
        'type' in value &&
        (value as LazyLoadValue).type === 'lazyLoad' &&
        'url' in value
    );
}

@Component({
    standalone: true,
    selector: 'qwyk-lazy-load-value',
    templateUrl: './lazy-load-value.component.html',
    styleUrls: ['./lazy-load-value.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, NgTemplateOutlet, FontAwesomeModule, TranslateModule],
})
export class LazyLoadValueComponent<T = unknown> implements OnInit, OnChanges {
    private readonly _queueService = inject(LazyLoadQueueService);
    private readonly _cdr = inject(ChangeDetectorRef);

    readonly faSpinner = faSpinner;
    readonly faDownload = faDownload;

    @Input() value: T | LazyLoadValue<T>;
    @Input() loadedTemplate: TemplateRef<{ $implicit: T }>;
    @Input() autoload = false;

    loading = false;
    loaded = false;
    loadedData: T | null = null;
    error: string | null = null;
    wasAutoloaded = false;

    get isLazyLoad(): boolean {
        return isLazyLoadValue(this.value);
    }

    get displayValue(): T {
        if (this.loaded && this.loadedData !== null) {
            return this.loadedData;
        }
        return this.value as T;
    }

    get showEmptyPlaceholder(): boolean {
        // Show dash when data was loaded (manually or auto) and is empty
        const emptyPlaceholderCondition1 = this.loaded && this._isEmpty(this.loadedData)
        // Show dash for non-lazy values that are empty when autoload is enabled
        const emptyPlaceholderCondition2 = this.autoload && !this.isLazyLoad && this._isEmpty(this.value)
        return emptyPlaceholderCondition1 || emptyPlaceholderCondition2;        
    }

    // Checks for various "empty" states
    private _isEmpty(val: unknown): boolean {
        if (val === null || val === undefined) return true;
        if (Array.isArray(val) && val.length === 0) return true;
        if (typeof val === 'string' && val.trim() === '') return true;
        return false;
    }

    ngOnInit(): void {
        if (this.autoload && this.isLazyLoad) {
            this.load(true);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['autoload'] &&
            changes['autoload'].currentValue === true &&
            this.isLazyLoad &&
            !this.loaded &&
            !this.loading
        ) {
            this.load(true);
        }
    }

    load(isAutoload = false): void {
        if (!isLazyLoadValue(this.value) || this.loading) {
            return;
        }

        this.wasAutoloaded = isAutoload;
        this.loading = true;
        this.error = null;
        this._cdr.markForCheck();

        this._queueService
            .load<{ data: T }>(this.value.url)
            .then((response) => {
                this.loadedData = response.data;
                this.loaded = true;
                this._cdr.detectChanges();
            })
            .catch((err) => {
                this.error = err?.message || 'Failed to load data';
                console.error('LazyLoadValue: Failed to load data', err);
                this._cdr.detectChanges();
            })
            .finally(() => {
                this.loading = false;
            });
    }
}
